/**
 * IANA timezone handling using the JS Intl API (Node and modern WebViews ship
 * full ICU with historical tz data, including e.g. pre-1947 Indian offsets).
 * No network access, no bundled tzdata.
 */

export interface LocalCivilTime {
  year: number;
  /** 1-12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
}

const dtfCache = new Map<string, Intl.DateTimeFormat>();

function getDtf(timeZone: string): Intl.DateTimeFormat {
  let dtf = dtfCache.get(timeZone);
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    dtfCache.set(timeZone, dtf);
  }
  return dtf;
}

/** Offset (minutes east of UTC) that `timeZone` applies at the given UTC instant. */
export function tzOffsetMinutes(utcMs: number, timeZone: string): number {
  const parts = getDtf(timeZone).formatToParts(new Date(utcMs));
  const map: Record<string, number> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = Number(p.value);
  }
  const asUtc = Date.UTC(
    map.year,
    map.month - 1,
    map.day,
    map.hour % 24, // some ICU builds render midnight as "24"
    map.minute,
    map.second,
  );
  return Math.round((asUtc - utcMs) / 60000);
}

/**
 * Convert a local civil time in an IANA timezone to a UTC Date.
 * Uses the standard two-pass estimation to handle DST transitions.
 */
export function zonedToUtc(local: LocalCivilTime, timeZone: string): Date {
  const guess = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, 0);
  const off1 = tzOffsetMinutes(guess, timeZone);
  let utc = guess - off1 * 60000;
  const off2 = tzOffsetMinutes(utc, timeZone);
  if (off2 !== off1) utc = guess - off2 * 60000;
  return new Date(utc);
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    getDtf(timeZone);
    return true;
  } catch {
    return false;
  }
}
